/**
 * Sticky booking sidebar for the public car-detail page.
 *
 * After the Iter 56 split, this file is purely a composition layer — it
 * owns the controlled state (received from the parent `CarDetail` page) and
 * stitches together four sub-components:
 *   - `BookingInsurancePicker`   — minimum-coverage + upsell plans + ⓘ popover.
 *   - `BookingDriverDetails`     — required identity fields + second-driver block.
 *   - `BookingPriceBreakdown`    — per-season rows, route/insurance/add-on fees, total.
 *   - `bookingHours.js` helpers  — time-window filtering for the pickup/return Selects.
 */
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SingleDatePicker } from "@/components/car/DateRangePicker";
import Money from "@/components/Money";
import { timeOptionsFor, hoursLabelFor } from "@/components/car/bookingHours";
import { BookingInsurancePicker } from "@/components/car/BookingInsurancePicker";
import { BookingDriverDetails } from "@/components/car/BookingDriverDetails";
import { BookingPriceBreakdown } from "@/components/car/BookingPriceBreakdown";
import BookingPaymentMethodPicker from "@/components/car/BookingPaymentMethodPicker";
import PickupPaymentMethods from "@/components/car/PickupPaymentMethods";
import BookingLoyaltyRedeem from "@/components/car/BookingLoyaltyRedeem";
import BookingAvailabilityCalendar from "@/components/car/BookingAvailabilityCalendar";
import BookingPoliciesPopup from "@/components/car/BookingPoliciesPopup";
import BookingTrustSignals from "@/components/car/BookingTrustSignals";
import { addOnPalette } from "@/components/car/addOnPalette";
import { MapPin, MapPinned, CalendarDays, Clock, Sparkles, Plane } from "lucide-react";

// Iter 67d: tiny colour-coded icon next to each booking-engine label. Matches the
// hero stepper's palette (indigo pickup-loc, violet dropoff-loc, amber pickup
// date, rose return date) so the funnel reads as one continuous flow.
function FieldLabel({ icon: Icon, color, children, testId }) {
  return (
    <Label className="text-xs uppercase tracking-widest text-neutral-500 flex items-center gap-1.5" data-testid={testId}>
      <Icon className={`h-3 w-3 ${color}`} aria-hidden="true" />
      {children}
    </Label>
  );
}

export default function BookingWidget({
  car,
  pickup, setPickup,
  dropoff, setDropoff,
  start, setStart,
  end, setEnd,
  pickupTime = "12:00", setPickupTime,
  returnTime = "12:00", setReturnTime,
  planId, setPlanId,
  addOnIds = [], setAddOnIds,
  bookedRanges = [],
  // Iter 67en+++++++ — renter's own active bookings across vendors;
  // overlayed on the calendar in soft amber so they catch a date
  // conflict before the warning popup fires.
  myBookedRanges = [],
  conflict,
  quote,
  loading,
  onBook,
  customerDetails = {}, setCustomerDetails,
  secondDriver = {}, setSecondDriver,
  termsAccepted = false, setTermsAccepted,
  vendorPaymentMethod = null, setVendorPaymentMethod,
  redeemPoints = 0, setRedeemPoints,
}) {
  const days = quote?.days || 0;
  const { t } = useTranslation();
  // Iter 67dj — host's policies (Terms / Cancellation / Privacy) now open
  // as a popup directly from the accept-terms row instead of opening a new
  // tab to /vendors/:id/policies. The clicked label decides which tab is
  // pre-selected when the dialog mounts.
  const [policiesOpen, setPoliciesOpen] = useState(false);
  const [policiesInitial, setPoliciesInitial] = useState("terms_and_conditions");
  const openPolicies = (section) => {
    setPoliciesInitial(section);
    setPoliciesOpen(true);
  };
  const PICKUP_TIME_OPTIONS = timeOptionsFor(car?.vendor, start);
  const RETURN_TIME_OPTIONS = timeOptionsFor(car?.vendor, end);
  const hoursLabel = hoursLabelFor(car?.vendor, start || end);

  // Per-vehicle minimum-rental-days guard (iter 45). Mirror the backend so we can disable
  // the Reserve button + inline-warn before the renter hits a 400.
  const minDays = Number(car?.minimum_rental_days) || 0;
  const belowMinimum = minDays > 0 && days > 0 && days < minDays;
  // Per-vehicle maximum-rental-days guard (iter 46). Long-term rentals go via the host's
  // messaging inbox for a custom quote.
  const maxDays = Number(car?.maximum_rental_days) || 0;
  const aboveMaximum = maxDays > 0 && days > maxDays;

  // Auto-clamp pickup/return time to the first valid in-window option whenever the vendor's
  // hours change and the currently-selected value falls outside the new window. Avoids the
  // controlled-value mismatch where the Select shows '10:00' even though it isn't pickable.
  useEffect(() => {
    if (setPickupTime && pickupTime && PICKUP_TIME_OPTIONS.length && !PICKUP_TIME_OPTIONS.includes(pickupTime)) {
      setPickupTime(PICKUP_TIME_OPTIONS[0]);
    }
    if (setReturnTime && returnTime && RETURN_TIME_OPTIONS.length && !RETURN_TIME_OPTIONS.includes(returnTime)) {
      setReturnTime(RETURN_TIME_OPTIONS[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [PICKUP_TIME_OPTIONS.join(","), RETURN_TIME_OPTIONS.join(",")]);

  const toggleAddOn = (id) => {
    if (!setAddOnIds) return;
    setAddOnIds(addOnIds.includes(id) ? addOnIds.filter((x) => x !== id) : [...addOnIds, id]);
  };

  // Iter 67bq — child seats (baby seat / booster / child seat / isofix /
  // infant seat) can be picked more than once. The backend `_resolve_add_ons`
  // counts duplicate IDs in the `add_on_ids` list and multiplies the fee
  // accordingly, so the UI just needs to push/pop the same ID.
  const isQuantifiableAddOn = (a) => {
    const n = (a?.name || "").toLowerCase();
    return /(baby seat|booster|child seat|infant seat|isofix|baby car seat)/.test(n);
  };
  const addOnQty = (id) => addOnIds.filter((x) => x === id).length;
  const incAddOn = (id) => {
    if (!setAddOnIds) return;
    setAddOnIds([...addOnIds, id]);
  };
  const decAddOn = (id) => {
    if (!setAddOnIds) return;
    const idx = addOnIds.indexOf(id);
    if (idx === -1) return;
    const next = addOnIds.slice();
    next.splice(idx, 1);
    setAddOnIds(next);
  };

  const reserveDisabled =
    loading || days <= 0 || !!conflict || belowMinimum || aboveMaximum ||
    (setTermsAccepted && !termsAccepted);

  return (
    <div className="sticky top-24 rounded-3xl border border-border p-6 shadow-sm" data-testid="booking-widget">
      <div className="flex items-baseline gap-1 mb-6">
        <Money amount={car.price_per_day} className="text-3xl font-display font-semibold" />
        <span className="text-neutral-500">{t("common.perDay")}</span>
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <FieldLabel icon={MapPin} color="text-indigo-600">{t("bookingWidget.pickupLocation")}</FieldLabel>
          <Select value={pickup} onValueChange={setPickup}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="booking-pickup-location"><SelectValue placeholder={t("bookingWidget.selectPickup")} /></SelectTrigger>
            <SelectContent>
              {(car.pickup_locations || []).map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
            </SelectContent>
          </Select>
          {/* Iter 67as — value-prop banner that appears the moment the renter
              switches the pickup to an airport venue. Reinforces why airport
              pickup is worth the (small) auto-applied Meet & Greet flat fee
              instead of letting it land cold in the price breakdown. */}
          {(() => {
            const isAirport = pickup && /\bairport\b/i.test(pickup);
            const fee = Number(car?.meet_greet_fee) || 0;
            if (!isAirport || fee <= 0) return null;
            return (
              <div
                className="mt-2 rounded-xl bg-gradient-to-br from-sky-50 via-white to-indigo-50/60 ring-1 ring-sky-200 px-3 py-2 flex items-start gap-2.5"
                data-testid="booking-meet-greet-banner"
              >
                <div className="h-7 w-7 shrink-0 rounded-lg bg-sky-100 ring-1 ring-sky-200 flex items-center justify-center">
                  <Plane className="h-3.5 w-3.5 text-sky-700" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-sky-900 flex items-center gap-1.5">
                    Meet & Greet · +<Money amount={fee} className="font-bold" />
                  </div>
                  <div className="text-[11px] text-sky-900/80 leading-snug mt-0.5">
                    Skip the queue — your host waits in the arrivals hall and walks you straight to the car.
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
        <div>
          <FieldLabel icon={MapPinned} color="text-violet-600">{t("bookingWidget.dropoffLocation")}</FieldLabel>
          <Select value={dropoff} onValueChange={setDropoff}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="booking-dropoff-location"><SelectValue placeholder={t("bookingWidget.selectDropoff")} /></SelectTrigger>
            <SelectContent>
              {(car.dropoff_locations || []).map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:items-end" data-testid="booking-pickup-row">
          <SingleDatePicker
            label={t("bookingWidget.pickup")}
            labelIcon={CalendarDays}
            labelIconColor="text-amber-600"
            value={start}
            onChange={setStart}
            bookedRanges={bookedRanges}
            myBookedRanges={myBookedRanges}
            testId="booking-start-date"
          />
          <div className="w-full sm:w-28">
            <FieldLabel icon={Clock} color="text-amber-600">{t("bookingWidget.time")}</FieldLabel>
            <Select value={pickupTime} onValueChange={setPickupTime || (() => {})}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="booking-pickup-time"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {PICKUP_TIME_OPTIONS.map((opt) => <SelectItem key={opt} value={opt} data-testid={`booking-pickup-time-${opt}`}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 sm:items-end" data-testid="booking-return-row">
          <SingleDatePicker
            label={t("bookingWidget.return")}
            labelIcon={CalendarDays}
            labelIconColor="text-rose-600"
            value={end}
            onChange={setEnd}
            min={start || undefined}
            bookedRanges={bookedRanges}
            myBookedRanges={myBookedRanges}
            testId="booking-end-date"
          />
          <div className="w-full sm:w-28">
            <FieldLabel icon={Clock} color="text-rose-600">{t("bookingWidget.time")}</FieldLabel>
            <Select value={returnTime} onValueChange={setReturnTime || (() => {})}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="booking-return-time"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {RETURN_TIME_OPTIONS.map((opt) => <SelectItem key={opt} value={opt} data-testid={`booking-return-time-${opt}`}>{opt}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {/* Iter 67dk — soft "Minimum N day(s)" chip explains why drop-off
            auto-snapped forward when the renter picked pickup. Only shown
            when the host enforces a minimum > 1 day. */}
        {minDays > 1 && (
          <div
            className="-mt-1 inline-flex items-center gap-1.5 self-start rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2.5 py-1 text-[11px] font-medium"
            data-testid="booking-min-rental-chip"
          >
            <CalendarDays className="h-3 w-3 text-amber-600" />
            {t("bookingWidget.minRentalChip", { count: minDays })}
          </div>
        )}
        {hoursLabel && (
          <div className="text-[11px] text-neutral-500 -mt-1" data-testid="vendor-hours-hint">
            {t("bookingWidget.hostOpens", { hours: hoursLabel })}
          </div>
        )}
      </div>

      {bookedRanges.length > 0 && (
        <BookingAvailabilityCalendar bookedRanges={bookedRanges} />
      )}

      <BookingInsurancePicker car={car} planId={planId} setPlanId={setPlanId} />

      {(car.add_ons || []).length > 0 && (
        <div className="space-y-2 mb-3" data-testid="booking-add-ons-picker">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-sky-500" aria-hidden="true" />
            {t("bookingWidget.addOnsTitle")}
          </div>
          {car.add_ons.map((a) => {
            const pal = addOnPalette(a);
            const quantifiable = isQuantifiableAddOn(a);
            const qty = addOnQty(a.id);
            const checked = quantifiable ? qty > 0 : addOnIds.includes(a.id);
            // Iter 67bq — for quantifiable seat-type add-ons the row collapses
            // the toggle into a "− qty +" stepper. Clicking the whole row when
            // qty=0 still adds the first instance (same affordance as the
            // legacy toggle); subsequent additions go through the stepper.
            const handleRowClick = () => {
              if (quantifiable) {
                if (qty === 0) incAddOn(a.id);
                return; // when qty>0 the stepper handles edits; click is a no-op
              }
              toggleAddOn(a.id);
            };
            return (
              <button
                key={a.id}
                type="button"
                onClick={handleRowClick}
                className={`w-full text-left rounded-xl border px-3 py-2.5 transition flex items-center justify-between gap-3 ${
                  checked
                    ? `${pal.ring} ${pal.bg}`
                    : "border-border hover:bg-neutral-50"
                }`}
                data-testid={`add-on-option-${a.id}`}
              >
                <div className="flex items-start gap-2.5 min-w-0">
                  <span
                    className={`mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ring-1 ${
                      checked
                        ? `${pal.medallion} ${pal.ring} ${pal.medallionFg}`
                        : "bg-neutral-100 border-neutral-200 text-neutral-500"
                    }`}
                    aria-hidden="true"
                  >
                    <pal.icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className={`text-sm font-medium truncate ${checked ? pal.fg : ""}`}>{a.name}</div>
                    {a.description && <div className="text-xs text-neutral-500 truncate">{a.description}</div>}
                  </div>
                </div>
                <div className="text-sm font-medium shrink-0 text-right flex items-center gap-2">
                  {quantifiable && qty > 0 ? (
                    // Stepper. Each button stops propagation so the parent row
                    // click doesn't toggle the whole add-on off.
                    <div
                      className={`inline-flex items-center gap-1 rounded-lg ring-1 ${pal.ring} ${pal.medallion} px-1 py-0.5`}
                      onClick={(e) => e.stopPropagation()}
                      role="group"
                      aria-label={`Quantity of ${a.name}`}
                      data-testid={`add-on-qty-${a.id}`}
                    >
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); decAddOn(a.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); decAddOn(a.id); } }}
                        className={`h-6 w-6 rounded-md flex items-center justify-center text-base font-bold leading-none cursor-pointer hover:bg-white/70 active:scale-95 transition ${pal.fg}`}
                        aria-label={`Remove one ${a.name}`}
                        data-testid={`add-on-qty-dec-${a.id}`}
                      >
                        −
                      </span>
                      <span className={`min-w-[1.5rem] text-center font-bold tabular-nums ${pal.fg}`} data-testid={`add-on-qty-value-${a.id}`}>{qty}</span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); incAddOn(a.id); }}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); incAddOn(a.id); } }}
                        className={`h-6 w-6 rounded-md flex items-center justify-center text-base font-bold leading-none cursor-pointer hover:bg-white/70 active:scale-95 transition ${pal.fg}`}
                        aria-label={`Add one ${a.name}`}
                        data-testid={`add-on-qty-inc-${a.id}`}
                      >
                        +
                      </span>
                    </div>
                  ) : (
                    <div>
                      <Money amount={a.price} />
                      <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-normal">
                        {a.pricing === "one_time" ? "one-time" : "/day"}
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {conflict && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-xs text-red-700 mb-3" data-testid="conflict-warning">
          {t("bookingWidget.conflictWarning", { start: conflict.start_date.slice(0, 10), end: conflict.end_date.slice(0, 10) })}
        </div>
      )}
      {belowMinimum && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 mb-3" data-testid="min-rental-warning">
          {t("bookingWidget.minRentalRequired", { count: minDays, days })}
        </div>
      )}
      {aboveMaximum && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 mb-3" data-testid="max-rental-warning">
          {t("bookingWidget.maxRentalExceeded", { count: maxDays, days })}
        </div>
      )}
      {Number(car?.minimum_driver_age) > 0 && (
        <div className="rounded-xl bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800 mb-3" data-testid="min-driver-age-notice">
          The host requires the primary driver to be at least <strong>{car.minimum_driver_age} years old</strong>. ID is verified at handover.
        </div>
      )}

      <BookingDriverDetails
        car={car}
        customerDetails={customerDetails}
        setCustomerDetails={setCustomerDetails}
        secondDriver={secondDriver}
        setSecondDriver={setSecondDriver}
        addOnIds={addOnIds}
      />

      {days > 0 && !conflict && <BookingPriceBreakdown car={car} quote={quote} />}
      {days > 0 && !conflict && setRedeemPoints && (
        <BookingLoyaltyRedeem
          rentalTotal={Number(quote?.total) || 0}
          value={redeemPoints}
          onChange={setRedeemPoints}
        />
      )}
      {days > 0 && !conflict && Array.isArray(car?.pickup_payment_methods_resolved) && car.pickup_payment_methods_resolved.length > 0 && (
        <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/30 px-3 py-2.5" data-testid="booking-pickup-methods-strip">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700 mb-1.5">
            Accepted at pick-up for this car
          </div>
          <PickupPaymentMethods methods={car.pickup_payment_methods_resolved} compact testId="booking-pickup-methods" />
        </div>
      )}
      {days > 0 && !conflict && setVendorPaymentMethod && (
        <BookingPaymentMethodPicker
          quote={quote}
          value={vendorPaymentMethod}
          onChange={setVendorPaymentMethod}
        />
      )}

      {/* Iter 67eb — Trust signals strip right before the Reserve CTA. */}
      <BookingTrustSignals />

      <Button
        onClick={onBook}
        disabled={reserveDisabled}
        className="rounded-full w-full mt-6 h-12 bg-gradient-to-r from-sky-600 via-indigo-600 to-violet-600 hover:from-sky-700 hover:via-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50"
        data-testid="book-now-btn"
      >
        {loading ? t("bookingWidget.redirecting") : conflict ? t("bookingWidget.datesUnavailable") : t("bookingWidget.reserve")}
      </Button>

      {setTermsAccepted && car.vendor?.id && (
        <label className="flex items-start gap-2 mt-4 text-xs text-neutral-600 cursor-pointer" data-testid="accept-terms-row">
          <input
            type="checkbox"
            checked={!!termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-input shrink-0"
            data-testid="accept-terms-checkbox"
          />
          <span className="leading-snug">
            {t("bookingWidget.acceptTermsPrefix")}{" "}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); openPolicies("terms_and_conditions"); }}
              className="text-blue-600 hover:underline font-medium"
              data-testid="accept-terms-link-terms"
            >
              {t("bookingWidget.acceptTermsLinkTerms")}
            </button>
            {", "}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); openPolicies("cancellation_policy"); }}
              className="text-blue-600 hover:underline font-medium"
              data-testid="accept-terms-link-cancellation"
            >
              {t("bookingWidget.acceptTermsLinkCancellation")}
            </button>
            {" & "}
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); openPolicies("privacy_policy"); }}
              className="text-blue-600 hover:underline font-medium"
              data-testid="accept-terms-link-privacy"
            >
              {t("bookingWidget.acceptTermsLinkPrivacy")}
            </button>
            .
          </span>
        </label>
      )}
      <BookingPoliciesPopup
        open={policiesOpen}
        onOpenChange={setPoliciesOpen}
        vendorId={car?.vendor?.id}
        initialSection={policiesInitial}
      />
      <div className="text-xs text-neutral-500 mt-3 text-center">{t("bookingWidget.noChargeYet")}</div>
    </div>
  );
}
